import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, CreditCard, Workflow, TrendingUp, Activity, Trophy, Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, sub, color = "klein-bg", testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-zinc-200 rounded-sm p-6"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 ${color} rounded-sm flex items-center justify-center text-white`}>
        <Icon size={16} />
      </div>
      <span className="overline text-zinc-500">{label}</span>
    </div>
    <p className="font-display text-3xl font-medium tracking-tighter">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </motion.div>
);

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    document.title = "Admin · FlowMind AI";
    if (user && !user.is_admin) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error("Impossible de charger les statistiques");
      }
    } finally {
      setLoading(false);
    }
  };

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="admin-forbidden">
        <ShieldAlert className="mx-auto text-red-500" size={48} />
        <h1 className="font-display text-3xl font-medium tracking-tighter mt-6">Accès réservé</h1>
        <p className="text-zinc-600 mt-2">Cette page est accessible uniquement aux administrateurs FlowMind AI.</p>
        <button
          onClick={() => navigate("/app/dashboard")}
          className="mt-6 px-5 py-2 bg-black text-white rounded-sm hover:bg-zinc-800"
        >
          Retour au tableau de bord
        </button>
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 flex items-center justify-center text-zinc-500">
        <Loader2 size={20} className="animate-spin mr-2" /> Chargement des statistiques…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="mb-10">
        <span className="overline">Admin</span>
        <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">Tableau de bord administrateur</h1>
        <p className="text-zinc-600 mt-2">Vue d'ensemble de l'activité FlowMind AI.</p>
      </div>

      {/* ===== Top KPIs ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} sub={`${stats.trial_users} en essai`} testid="admin-stat-users" />
        <StatCard icon={CreditCard} label="Abonnements actifs" value={stats.active_subscriptions} sub={`sur ${stats.total_users} comptes`} color="bg-emerald-600" testid="admin-stat-subs" />
        <StatCard icon={TrendingUp} label="Revenus du mois" value={`${stats.revenue_month_eur.toLocaleString("fr-FR")} €`} sub={`${stats.invoices_month} factures`} color="bg-black" testid="admin-stat-revenue" />
        <StatCard icon={Workflow} label="Workflows créés" value={stats.workflows_total} sub="total cumulé" color="bg-amber-500" testid="admin-stat-workflows" />
      </div>

      {/* ===== Plan breakdown ===== */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={18} className="text-zinc-500" />
          <h2 className="font-display text-xl font-medium tracking-tight">Répartition par plan</h2>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
          {stats.plan_breakdown.map((p) => (
            <div key={p.plan} className="flex items-center justify-between px-6 py-4" data-testid={`admin-plan-${p.plan}`}>
              <span className="capitalize font-medium">{p.plan}</span>
              <span className="font-mono text-sm text-zinc-700">{p.count} utilisateurs</span>
            </div>
          ))}
          {stats.plan_breakdown.length === 0 && (
            <p className="px-6 py-4 text-sm text-zinc-500">Aucune donnée encore.</p>
          )}
        </div>
      </section>

      {/* ===== Vote ranking ===== */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={18} className="text-amber-500" />
          <h2 className="font-display text-xl font-medium tracking-tight">Classement des intégrations demandées</h2>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="admin-votes-ranking">
          {stats.votes_ranking.length === 0 && (
            <p className="px-6 py-6 text-sm text-zinc-500">Aucun vote pour le moment. Encouragez vos utilisateurs à voter !</p>
          )}
          {stats.votes_ranking.map((v, i) => (
            <div key={v.slug} className="flex items-center justify-between px-6 py-4" data-testid={`admin-vote-row-${v.slug}`}>
              <div className="flex items-center gap-4">
                <span className={`font-display text-xl ${i < 3 ? "text-amber-600" : "text-zinc-400"} w-8`}>#{i + 1}</span>
                <span className="font-medium">{v.name}</span>
              </div>
              <span className="font-mono text-sm bg-zinc-100 px-3 py-1 rounded-sm">{v.votes} vote{v.votes > 1 ? "s" : ""}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Recent signups ===== */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} className="text-zinc-500" />
          <h2 className="font-display text-xl font-medium tracking-tight">Derniers inscrits</h2>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
          {stats.recent_signups.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-6 py-3" data-testid={`admin-signup-${u.id}`}>
              <div>
                <p className="font-medium">{u.full_name}</p>
                <p className="text-xs text-zinc-500">{u.email}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 capitalize font-mono">{u.plan}</p>
                <p className="text-xs text-zinc-400">{new Date(u.created_at).toLocaleDateString("fr-FR")}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
